import { useEffect, useRef, useState } from 'react';
import {
  startBatchAnalysis,
  getBatchStatus,
  getBatchResults,
  compressImage,
  fileToBase64
} from '@/services/api';
import { BatchAnalysisResult } from '@/types';

type BatchStatus = 'idle' | 'processing' | 'completed' | 'failed';

export function BatchAnalysis() {
  const [files, setFiles] = useState<File[]>([]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [status, setStatus] = useState<BatchStatus>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [results, setResults] = useState<BatchAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const next = Array.from(fileList).filter(f => f.type.startsWith('image/')).slice(0, 50);
    setFiles(next);
    setResults(null);
    setStatus('idle');
    setProgress(0);
    setBatchId(null);
    setError(null);
  };

  const convertFilesToBase64 = async (input: File[]): Promise<string[]> => {
    const encoded: string[] = [];
    for (const file of input) {
      const compressed = await compressImage(file, 1200, 0.9);
      const b64 = await fileToBase64(compressed);
      encoded.push(b64);
    }
    return encoded;
  };

  const startPolling = (id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      try {
        const statusResp = await getBatchStatus(id);
        if (statusResp?.progress !== undefined) {
          setProgress(Math.min(100, Math.round(statusResp.progress)));
        }
        if (statusResp?.status === 'completed') {
          clearInterval(pollRef.current || undefined);
          const res = await getBatchResults(id);
          setResults(res);
          setStatus('completed');
        } else if (statusResp?.status === 'failed') {
          clearInterval(pollRef.current || undefined);
          setStatus('failed');
          setError(statusResp?.error || 'Batch failed');
        } else {
          setStatus('processing');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to fetch batch status');
      }
    }, 1500);
  };

  const handleStart = async () => {
    if (files.length === 0) {
      setError('Please choose up to 50 images.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setResults(null);
    setStatus('processing');
    setProgress(2);

    try {
      const encoded = await convertFilesToBase64(files);
      const resp = await startBatchAnalysis(encoded, 'quick_scan', 'en');
      const id = resp.batch_id || `batch_${Date.now()}`;
      setBatchId(id);
      startPolling(id);
    } catch (err) {
      console.error(err);
      setError('Unable to start batch analysis. Please try again.');
      setStatus('failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportJSON = () => {
    if (!results) return;
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-${results.batch_id || 'results'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    if (!results) return;
    const header = ['image_index', 'status', 'concern', 'confidence', 'error'];
    const lines = results.results.map(r => {
      const concern = r.analysis?.primary_findings?.[0]?.concern ?? '';
      const confidence = r.analysis?.primary_findings?.[0]?.confidence ?? '';
      return [r.image_index, r.status, concern, confidence, r.error || ''].join(',');
    });
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-${results.batch_id || 'results'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Batch Analysis</h2>
          <p className="text-sm text-gray-600">Upload a folder (max 50 images) for quick multi-image analysis.</p>
        </div>
        {batchId && (
          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">Batch: {batchId}</span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <label className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-violet-400 transition">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
          />
          <div className="text-gray-700 font-medium">Select images</div>
          <div className="text-xs text-gray-500 mt-1">Up to 50 images</div>
        </label>
        <label className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-violet-400 transition">
          {/* @ts-expect-error directory upload (webkit) */}
          <input
            type="file"
            multiple
            webkitdirectory="true"
            directory="true"
            accept="image/*"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
          />
          <div className="text-gray-700 font-medium">Upload a folder</div>
          <div className="text-xs text-gray-500 mt-1">Keeps file order for indexing</div>
        </label>
      </div>

      {files.length > 0 && (
        <div className="mb-4 max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-3">
          <div className="flex items-center justify-between text-sm text-gray-700 mb-2">
            <span>{files.length} files selected</span>
            <button
              className="text-xs text-violet-600 hover:underline"
              onClick={() => setFiles([])}
            >
              Clear
            </button>
          </div>
          <ul className="text-xs text-gray-600 space-y-1">
            {files.map((f, i) => (
              <li key={i}>{i + 1}. {f.name} ({Math.round(f.size / 1024)} KB)</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center space-x-3 mb-6">
        <button
          onClick={handleStart}
          disabled={isSubmitting || files.length === 0}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium disabled:opacity-60"
        >
          {isSubmitting ? 'Starting…' : 'Start Batch Analysis'}
        </button>
        {status === 'processing' && (
          <div className="flex-1">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Processing… {progress}%</p>
          </div>
        )}
        {status === 'completed' && (
          <span className="text-xs text-green-600 font-medium">Completed</span>
        )}
        {status === 'failed' && (
          <span className="text-xs text-red-600 font-medium">Failed</span>
        )}
      </div>

      {results && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Processed {results.total_images} images • {results.successful} success • {results.failed} failed
              </p>
              <p className="text-xs text-gray-500">{results.summary}</p>
            </div>
            <div className="space-x-2">
              <button onClick={exportJSON} className="text-xs px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">Export JSON</button>
              <button onClick={exportCSV} className="text-xs px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">Export CSV</button>
            </div>
          </div>

          <div className="border border-gray-100 rounded-lg divide-y">
            {results.results.map((r) => (
              <div key={r.image_index} className="p-3 flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Image #{r.image_index + 1}</p>
                  <p className="text-xs text-gray-600">Status: {r.status}</p>
                  {r.error && <p className="text-xs text-red-600">Error: {r.error}</p>}
                  {r.analysis?.primary_findings?.length > 0 && (
                    <div className="text-xs text-gray-700 mt-1">
                      <p className="font-semibold">Top Finding:</p>
                      <p>{r.analysis.primary_findings[0].concern} ({Math.round((r.analysis.primary_findings[0].confidence || 0) * 100)}%)</p>
                    </div>
                  )}
                </div>
                {r.analysis?.requires_detailed_analysis && (
                  <span className="text-[10px] px-2 py-1 bg-amber-100 text-amber-700 rounded-full">Review</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
